import {FC, useState} from "react";
import {TextInput} from "@interstate/components/TextInput";
import {useOperationalEntity} from "../../context";
import {
  LookupOperationalEntity,
  LookupOperationalEntityData,
  LookupOperationalEntityVariables,
  UpdateOperationalEntitySources,
  UpdateOperationalEntitySourcesData,
  UpdateOperationalEntitySourcesVariables,
  assertOperationalEntityPresent
} from "../../backend";
import {
  Source,
  UpdateSourcesInput
} from "@common-core/coat-operational-hierarchy-appsync-model";
import {Endpoints} from "../../../runtime";
import {useGraphqlLazyQuery, useGraphqlMutation} from "../../../amplify/hooks";
import {
  lookupOperationalEntity as lookupOperationalEntityQuery,
  updateOperationalEntitySources as updateSourcesMutation
} from "../../../amplify/graphql";
import {useTranslation} from "react-i18next";
import {ErrorHandler, OnCompleteCallback} from "../../../utils";
import {ModalWithAlert, useAlert} from "../../../alerting";
import "./EditSourcesModal.scss";

export const EditSourcesModal: FC<{
  id: string;
  initalSources: Source[];
  onComplete: OnCompleteCallback;
}> = ({id, initalSources, onComplete}) => {
  const {t} = useTranslation();
  const {alert, setAlert, createErrorAlert} = useAlert({
    errorTitleKey: "toast.backend-error"
  });
  const [inputErrorMessage, setInputErrorMessage] = useState<string>();
  const [working, setWorking] = useState(false);
  const [sources, setSources] = useState(initalSources);
  const {entity, setEntity} = useOperationalEntity();

  const [lookUpOperationalEntity] = useGraphqlLazyQuery<
    LookupOperationalEntityData,
    LookupOperationalEntityVariables
  >({
    document: LookupOperationalEntity,
    amplifyQuery: lookupOperationalEntityQuery,
    context: {endpoint: Endpoints.APPSYNC},
    fetchPolicy: "network-only"
  });

  const [updateSources] = useGraphqlMutation<
    UpdateOperationalEntitySourcesData,
    UpdateOperationalEntitySourcesVariables
  >({
    document: UpdateOperationalEntitySources,
    amplifyMutation: updateSourcesMutation,
    context: {endpoint: Endpoints.APPSYNC}
  });

  const clearErrors = (): void => {
    setAlert(undefined);
    setInputErrorMessage(undefined);
  };

  const handleError: ErrorHandler = (error: any) => {
    if (!error.graphQLErrors) {
      createErrorAlert(error);
      return;
    }
    const clientValidationError = error.graphQLErrors.find(
      (graphQLerror: any) => graphQLerror.errorType == "ClientValidationError"
    );
    if (clientValidationError) {
      const invalidIds = clientValidationError.errorInfo.invalidIds;
      let errorMessage = "Please correct your entry. CACM ID";

      switch (invalidIds.length) {
        case 0:
          errorMessage += " not found";
          break;
        case 1:
          errorMessage += `: ${invalidIds[0]} was not found`;
          break;
        default: {
          const lastId = invalidIds.pop();
          errorMessage += `'s: ${invalidIds.join(", ")}, and ${lastId} were not found`;
          break;
        }
      }
      setInputErrorMessage(errorMessage);
    } else {
      createErrorAlert(error);
    }
  };

  const refreshEntity = async (): Promise<void> => {
    return await lookUpOperationalEntity({variables: {id: entity.id}})
      .then(assertOperationalEntityPresent)
      .then(setEntity);
  };

  const saveChanges = async () => {
    setWorking(true);
    clearErrors();
    const sourcesInput: UpdateSourcesInput = {entityId: id, sources};
    await updateSources({variables: {sourcesInput}})
      .then(result => {
        if (result.data?.result.success === false) {
          throw new Error(t("common-errors.backend-error"));
        }
      })
      .then(refreshEntity)
      // If the update call returns an error, give the user the opportunity to try again
      .then(() => onComplete(true))
      .catch(handleError)
      .finally(() => setWorking(false));
  };

  const cancelChanges = () => {
    onComplete(false);
  };

  const onInputChange = (event: any) => {
    event.target?.value.trim() === ""
      ? setSources([])
      : setSources([
          {sourceId: event.target?.value.trim(), sourceType: "CACM"}
        ]);
  };

  return (
    <ModalWithAlert
      id={"edit-source-modal"}
      data-testid={"edit-source-modal"}
      show={true}
      size={"large"}
      header={t("modal.header.edit-source")}
      alert={alert}
      footer={{
        primary: {
          action: saveChanges,
          label: t("common-actions.save-changes"),
          isLoading: working
        },
        options: {
          action: cancelChanges,
          label: t("common-actions.cancel"),
          isLoading: working,
          buttonStyle: "tertiary"
        }
      }}
      onHide={cancelChanges}>
      <TextInput
        id={"source-text-input"}
        data-testid={"source-text-input"}
        name={"parent"}
        label={`${t("modal.text-input.edit-source")}`}
        value={sources[0] ? sources[0].sourceId : ""}
        onChange={onInputChange}
        onKeyDown={async (event: any) => {
          if (event.key === "Enter") {
            await saveChanges();
          }
        }}
        clearButton={!!sources[0]}
        hasError={!!inputErrorMessage}
        errorMessage={inputErrorMessage}
      />
    </ModalWithAlert>
  );
};
